/**
 * Footer component
 * Updated to use centralized chain config
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { ReactComponent as Help } from "../../assets/svg/help.svg";
import { ReactComponent as Twitter } from "../../assets/svg/twitter.svg";
import { ReactComponent as Discord } from "../../assets/svg/discord.svg";
import { ReactComponent as Github } from "../../assets/svg/github.svg";
import { LAYER_COLORS, TEXT_COLORS } from "../../constants/design/colors";
import { SPACING } from "../../constants/design/spacing";
import { FONT_SIZE } from "../../constants/design/fontSize";
import { BUILD_VERSION, BUILD_TIME, COMMIT_HASH } from "../../buildInfo";
import { useIntercomHook } from "../../hooks/intercom";
import { LINKS } from "../../constants/links";
import ExternalLinkText from "../ExternalLinkText";
import UptimeMonitor from "../UptimeMonitor";
import CurrentTimeUTC from "../UptimeMonitor/CurrentTimeUTC";
import MaintenanceSchedule from "../UptimeMonitor/MaintenanceSchedule";
import { Divider } from "../shared/Divider";
import {
  BlockContainer,
  CenterContainer,
  Container,
  LeftContainer,
  RightContainer,
  SocialIconLink,
  SocialIconsWrapper,
} from "./style";
import { HintBar } from "../keyboard/HintBar";
import {
  BLOCK_EXPLORER_URL,
  CHAIN_NAME,
  RPC_WS_URLS,
  SKIP_EXPLORER_BLOCK_API,
  MAINTENANCE_TIMESTAMP,
  parseWebSocketBlockNumber,
  fetchExplorerBlockNumber,
  fetchRpcBlockNumber,
} from "./helpers";

function useRealtimeBlockNumber(): number | undefined {
  const [blockNumber, setBlockNumber] = useState<number | undefined>();

  const updateBlockNumber = useCallback((nextBlockNumber?: number) => {
    if (!nextBlockNumber) return;
    setBlockNumber((currentBlockNumber) =>
      !currentBlockNumber || nextBlockNumber > currentBlockNumber
        ? nextBlockNumber
        : currentBlockNumber
    );
  }, []);

  const { data: fallbackBlockNumber } = useSWR(
    ["BlockNumberFallback"],
    async () => {
      // The chain's own RPC first — it is the source of truth for block height
      // — and the explorer only if every RPC fails. Explorer-first meant a
      // request to e.g. testnet.arcscan.app/api every 30s from every tab; it
      // rate-limits (429 on every page load on Arc, 2026-09-11), and the
      // OpenTelemetry zone patch a wallet SDK installs logs that rejection to
      // the console even though it is caught here.
      const fromRpc = await fetchRpcBlockNumber();
      if (fromRpc || SKIP_EXPLORER_BLOCK_API) return fromRpc;
      return fetchExplorerBlockNumber().catch(() => undefined);
    },
    {
      refreshInterval: 30000,
      onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
        setTimeout(() => revalidate({ retryCount }), 30000);
      },
    }
  );

  useEffect(() => {
    updateBlockNumber(fallbackBlockNumber);
  }, [fallbackBlockNumber, updateBlockNumber]);

  useEffect(() => {
    if (typeof window === "undefined" || RPC_WS_URLS.length === 0) return;

    let stopped = false;
    let websocket: WebSocket | undefined;
    let reconnectTimer: number | undefined;
    let urlIndex = 0;

    const connect = () => {
      if (stopped) return;
      const rpcWsUrl = RPC_WS_URLS[urlIndex % RPC_WS_URLS.length];
      urlIndex += 1;

      try {
        websocket = new WebSocket(rpcWsUrl);
      } catch {
        reconnectTimer = window.setTimeout(connect, 2000);
        return;
      }

      websocket.onopen = () => {
        websocket?.send(
          JSON.stringify({
            jsonrpc: "2.0",
            id: 1,
            method: "eth_subscribe",
            params: ["newHeads"],
          })
        );
      };

      websocket.onmessage = (event) => {
        if (typeof event.data !== "string") return;
        updateBlockNumber(parseWebSocketBlockNumber(event.data));
      };

      websocket.onerror = () => {
        websocket?.close();
      };

      websocket.onclose = () => {
        if (!stopped) {
          reconnectTimer = window.setTimeout(connect, 2000);
        }
      };
    };

    connect();

    return () => {
      stopped = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      websocket?.close();
    };
  }, [updateBlockNumber]);

  return blockNumber;
}

function Footer() {
  const { t } = useTranslation("app", { keyPrefix: "Footer" });
  const { handleIntercomButtonClicked } = useIntercomHook();

  const maintenanceInProgress = Number(MAINTENANCE_TIMESTAMP)
    ? Number(MAINTENANCE_TIMESTAMP) * 1000 <= Date.now()
    : false;

  const blockNumber = useRealtimeBlockNumber();

  return (
    <Container>
      <LeftContainer>
        <UptimeMonitor
          maintenanceTimestampSeconds={Number(MAINTENANCE_TIMESTAMP)}
        />
        <Divider
          direction="vertical"
          size="16px"
          style={{
            backgroundColor: LAYER_COLORS.four,
            margin: `0 ${SPACING.three}px`,
          }}
        />
        {Boolean(Number(MAINTENANCE_TIMESTAMP) && !maintenanceInProgress) && (
          <>
            <MaintenanceSchedule
              maintenanceTimestampSeconds={Number(MAINTENANCE_TIMESTAMP)}
            />
            <Divider
              direction="vertical"
              size="16px"
              style={{
                backgroundColor: LAYER_COLORS.four,
                margin: `0 ${SPACING.three}px`,
              }}
            />
          </>
        )}
        <CurrentTimeUTC />
      </LeftContainer>
      <CenterContainer>
        <HintBar />
      </CenterContainer>
      <RightContainer>
        <SocialIconsWrapper>
          <SocialIconLink
            href={LINKS.twitter}
            target="_blank"
            rel="noreferrer"
            title="Twitter"
          >
            <Twitter />
          </SocialIconLink>
          <SocialIconLink
            href={LINKS.discord}
            target="_blank"
            rel="noreferrer"
            title="Discord"
          >
            <Discord />
          </SocialIconLink>
          <SocialIconLink
            href={LINKS.github}
            target="_blank"
            rel="noreferrer"
            title="GitHub"
          >
            <Github />
          </SocialIconLink>
        </SocialIconsWrapper>
        <Divider
          direction="vertical"
          size="16px"
          style={{
            backgroundColor: LAYER_COLORS.four,
            margin: `0 ${SPACING.three}px`,
          }}
        />
        <ExternalLinkText
          disableHoverAnimation
          color={TEXT_COLORS.three}
          hoverColor={TEXT_COLORS.one}
          role="button"
          hideArrow
          onClick={handleIntercomButtonClicked}
        >
          <Help
            style={{
              marginRight: SPACING.one,
            }}
          />
          {t("help_and_support")}
        </ExternalLinkText>
        <Divider
          direction="vertical"
          size="16px"
          style={{
            backgroundColor: LAYER_COLORS.four,
            margin: `0 ${SPACING.three}px`,
          }}
        />
        <BlockContainer
          href={
            blockNumber
              ? `${BLOCK_EXPLORER_URL}/block/${blockNumber}`
              : BLOCK_EXPLORER_URL
          }
          target="_blank"
          rel="noreferrer"
        >
          <span>{CHAIN_NAME} Block:</span>
          &nbsp;
          <span>{!blockNumber ? "---" : blockNumber}</span>
        </BlockContainer>
        <Divider
          direction="vertical"
          size="16px"
          style={{
            backgroundColor: LAYER_COLORS.four,
            margin: `0 ${SPACING.three}px`,
          }}
        />
        <span
          title={`commit ${COMMIT_HASH}\nbuilt ${BUILD_TIME}`}
          style={{
            color: TEXT_COLORS.three,
            fontSize: FONT_SIZE.xs,
            fontFamily: "'JetBrains Mono', monospace",
            userSelect: "text",
          }}
        >
          v{BUILD_VERSION}
        </span>
      </RightContainer>
    </Container>
  );
}

export default Footer;
